import AddressResultItem from '@/experiences/sales-portal/components/address-search-input/address-result-item';
import AddressSearchEmptyState from '@/experiences/sales-portal/components/address-search-input/address-search-empty-state';
import AddressSearchLoadingState from '@/experiences/sales-portal/components/address-search-input/address-search-loading-state';
import i18n from '@/utils/i18n';
import React, { useCallback, useRef, useState } from 'react';
import { AddressSearchInputProps } from '@/experiences/sales-portal/components/address-search-input/types';
import { AddressSearchResult } from '@/experiences/sales-portal/components/address-search-input/use-google-api-address-search-loader';
import { createStyles } from '@/contexts/theme-context';
import {
  FlatList,
  LayoutChangeEvent,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  View,
} from 'react-native';
import { InputCompound, Popover } from '@usepepper/sprout-kit-ui';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';

export const AddressSearchInput: React.FC<AddressSearchInputProps> = ({
  isEmpty = false,
  isLoading = false,
  onClose,
  onSearch,
  onSelect,
  results,
  value,
}) => {
  const [styles] = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>();
  const anchorRef = useRef<View>(null);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      onClose?.(open);
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleChangeText = useCallback(
    (text: string) => {
      onSearch(text);
      if (!isOpen) {
        setIsOpen(true);
      }
    },
    [isOpen, onSearch],
  );

  const handleSelect = useCallback(
    (item: AddressSearchResult) => {
      onSelect(item);
      setIsOpen(false);
    },
    [onSelect],
  );

  const handleInputShortcuts = (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => {
    if (e.nativeEvent.key === Key.Escape) {
      setIsOpen(false);
    }
  };

  const handleAnchorLayout = (event: LayoutChangeEvent) => {
    setAnchorWidth(event.nativeEvent.layout.width);
  };

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      <Popover.Anchor asChild>
        <View
          onLayout={handleAnchorLayout}
          ref={anchorRef}
          style={styles.anchorContainer}
        >
          <InputCompound.Area
            onChangeText={handleChangeText}
            onFocus={handleFocus}
            onKeyPress={handleInputShortcuts}
            placeholder={i18n.t(
              'sales_portal.edit_account.placeholders.address',
            )}
            value={value}
          />
        </View>
      </Popover.Anchor>
      <Popover.Content style={styles.popoverContainer}>
        {isLoading ? (
          <AddressSearchLoadingState
            style={anchorWidth ? { width: anchorWidth } : undefined}
          />
        ) : (
          <FlatList
            data={results}
            keyExtractor={(item) => item.key}
            ListEmptyComponent={isEmpty ? <AddressSearchEmptyState /> : null}
            renderItem={({ item }) => (
              <AddressResultItem item={item} onSelect={handleSelect} />
            )}
            scrollEnabled
            style={[...(anchorWidth ? [{ width: anchorWidth }] : [])]}
          />
        )}
      </Popover.Content>
    </Popover>
  );
};

const useStyles = createStyles((theme) => ({
  anchorContainer: {
    flex: 1,
    width: '100%',
  },
  popoverContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    boxShadow: '0 12px 24px 0 rgba(0, 0, 0, 0.10)',
    maxHeight: 300,
  },
}));
